import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Users,
  ListOrdered,
  Mail,
  Calendar,
  UserCheck,
  Database,
  Edit3,
  LayoutDashboard,
  TestTube,
  Heart,
  Image,
  Target,
  Lock,
} from 'lucide-react'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Rollout & Customer Journey (Call Notes)
   Password-protected. SIDEPIECE and Ryan immediate actions highlighted.
   ═══════════════════════════════════════════════════════════════════════════ */

const MASTER_PLAN_PASSWORD = 'betadizee101'

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string }> = {
  sky: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-sky-500/15 text-sky-200' },
  violet: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-violet-500/15 text-violet-200' },
  emerald: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-emerald-500/15 text-emerald-200' },
  amber: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-amber-500/15 text-amber-200' },
  cyan: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-cyan-500/15 text-cyan-200' },
  rose: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-rose-500/15 text-rose-200' },
  zinc: { border: 'border-white/[0.06]', bg: 'bg-white/[0.015]', text: 'text-zinc-400', pill: 'bg-white/[0.08] text-zinc-300' },
}

type HighlightType = 'sidepiece' | 'ryan'

interface SectionContentList {
  type: 'list'
  items: (string | { text: string; highlight?: HighlightType })[]
}
interface SectionContentPara {
  type: 'para'
  text: string
}
interface SectionContentTable {
  type: 'table'
  headers: string[]
  rows: string[][]
}
interface SectionContentHeading {
  type: 'heading'
  level: 1 | 2
  text: string
}
interface SectionContentSubheading {
  type: 'subheading'
  text: string
}

type RolloutSectionBlock =
  | SectionContentList
  | SectionContentPara
  | SectionContentTable
  | SectionContentHeading
  | SectionContentSubheading

interface RolloutSection {
  id: string
  number: number
  icon: typeof Users
  title: string
  subtitle?: string
  color: string
  blocks: RolloutSectionBlock[]
}

const sections: RolloutSection[] = [
  {
    id: 'presentation',
    number: 1,
    icon: Image,
    title: 'Presentation & marketing (release links, Dizee brand)',
    color: 'violet',
    blocks: [
      { type: 'para', text: 'Black background + **Dizee** pill/badge on phone — looks thick and polished. When sending to people: "This is what Dizee is."' },
      { type: 'subheading', text: 'Two-step story' },
      {
        type: 'list',
        items: [
          'First: core product (Dizee). If reception is good \u2192 follow up with Dizee links: "Once your Dizee profile is set up, these are Dizee links — how you do everything; looks great, works in Dizee; all info ready to set up any of these links."',
          'Lead strategy: if Dizee links are clearly relevant (e.g. manager/artist), lead with links; otherwise lead with core product.',
          { text: 'Use smaller / less critical contacts as pilots to test the flow and how to start the conversation (e.g. one manager, one Spanish artist); refine before bigger fish and labels.', highlight: 'ryan' },
        ],
      },
    ],
  },
  {
    id: 'onboarding',
    number: 2,
    icon: Mail,
    title: 'Onboarding & customer journey',
    color: 'sky',
    blocks: [
      { type: 'para', text: '**Right after demo:** Send onboarding email within 20–30 min: link, password, simple instructions. "Complete these 3–5 steps"; explain why each step and what it does.' },
      { type: 'para', text: '**Follow-up:** 2–5 days later — "Did you set up? How\'s it going?" Optional: another call, invite to WhatsApp.' },
      { type: 'para', text: 'Map journeys per segment (warm vs cold); define emails and touchpoints for each. Now: personal, manual emails. Later: same journey in signup flow (welcome email, steps, follow-up).' },
    ],
  },
  {
    id: 'rollout-strategy',
    number: 3,
    icon: Target,
    title: 'Rollout strategy: promoter-first',
    color: 'emerald',
    blocks: [
      { type: 'para', text: "Don't lead with 'all managers'. Lead with **one promoter** (e.g. Thomas Turner); get them on board first." },
      { type: 'subheading', text: 'Flow' },
      {
        type: 'list',
        items: [
          'Promoter agrees to use Dizee and send ticket counts.',
          'Use their upcoming shows to identify managers/artists.',
          { text: 'Tell those managers: "Thomas Turner will be the first promoter; next week he\'ll start sending ticket counts; you\'ll be one of the first to get these emails — here\'s what they\'ll say and do."', highlight: 'ryan' },
          'Promoter starts sending; managers get email: "Your tickets are being tracked on Dizee — log in or sign up to see real-time analytics and help the show sell out."',
          'They sign up \u2192 in workspace \u2192 then do a demo and explain more (unveil other sections).',
        ],
      },
      { type: 'para', text: '**Why this first:** Ticket tracking = unique (no other solution). Release links = second hook. Streaming/writer credits = third (available elsewhere).' },
      {
        type: 'list',
        items: [
          { text: 'Start narrow: promoter only enters tickets for SIDEPIECE first; SIDEPIECE team linked. Then add one more artist, then another, then "go ahead, do all your ticket accounts."', highlight: 'sidepiece' },
          '500 users in a few months is realistic if we deliver real value.',
        ],
      },
    ],
  },
  {
    id: 'timeline',
    number: 4,
    icon: Calendar,
    title: 'Timeline & urgency',
    color: 'amber',
    blocks: [
      { type: 'para', text: '**Target:** ~March 20 — ready to drop a release link (e.g. Charlie) and have everything in motion.' },
      {
        type: 'list',
        items: [
          { text: '~1 week: get it in the hands of the SIDEPIECE team.', highlight: 'sidepiece' },
          { text: '1–2 weeks after: Thomas Turner (promoter) move.', highlight: 'ryan' },
          'By ~March 20: "go time" — system ready for thousands of clicks via release link if needed.',
        ],
      },
      { type: 'para', text: 'Put the plan on paper, lock it with the team, set a timeline and goal: "This is the plan; make it happen."' },
      { type: 'para', text: 'Prerequisites: team informed on data and platform; staging, analytics, QA so we can handle load.' },
    ],
  },
  {
    id: 'sidepiece-onboarding',
    number: 5,
    icon: UserCheck,
    title: 'SIDEPIECE onboarding (step 1)',
    color: 'rose',
    blocks: [
      {
        type: 'list',
        items: [
          { text: 'Who: 4 managers, 2 artists (Dylan, Ricky), marketing (Paul). We create accounts for them.', highlight: 'sidepiece' },
          { text: 'We set up accounts, emails, permissions; tell them: "Go to Dizee, use your email, you\'ll get a code." No training on loading data — we load it.', highlight: 'sidepiece' },
          { text: 'Main SIDEPIECE artist account: only Ryan + Eddie for now; others don\'t need to log into it; if someone needs to be added, they ask Ryan.', highlight: 'sidepiece' },
          { text: 'When something is relevant, "it\'s on Dizee"; no big training moment — it becomes the shared source of truth.', highlight: 'sidepiece' },
          'Same pattern for cold users later: manager has main account and gives artist (and others) access as needed.',
        ],
      },
    ],
  },
  {
    id: 'data-to-load',
    number: 6,
    icon: Database,
    title: 'Data to load (SIDEPIECE)',
    color: 'cyan',
    blocks: [
      {
        type: 'list',
        items: [
          { text: '**Music:** Already in from onboarding (Spotify); done.', highlight: 'sidepiece' },
          { text: '**Shows:** Bandsintown already in; upload Wasserman spreadsheet; system merges/dedupes (Wasserman = source of truth for real data).', highlight: 'sidepiece' },
          { text: '**Money:** Log what needs to get paid; manual for now.', highlight: 'sidepiece' },
          { text: '**Tasks:** Master task list; create and assign tasks before people have access so when they log in, "assigned to me" is already there.', highlight: 'sidepiece' },
          { text: '**Calendar:** Create shared SIDEPIECE calendar (everyone shared); link to artist account; anything added reflects on Dizee. Recommend this to any team.', highlight: 'sidepiece' },
          { text: '**Google Sheet:** SIDEPIECE may not need it — add tour managers to Shows so they see in real time; replaces sheet. Charlie: agent keeps using their sheet; data flows into Dizee; team views in Dizee (non-invasive path in).', highlight: 'sidepiece' },
        ],
      },
    ],
  },
  {
    id: 'who-updates',
    number: 7,
    icon: Edit3,
    title: 'Who updates what',
    color: 'emerald',
    blocks: [
      {
        type: 'list',
        items: [
          '**Shows:** Option A — one designated person (e.g. Ryan or Alex) adds shows. Option B — anyone can add. Suggest "designated" so someone owns it; one-sheet: "Two ways: designate one person OR anyone can update — we suggest designate."',
          { text: '**Music:** For SIDEPIECE, Ryan designated (releases, music section).', highlight: 'ryan' },
          '**Tasks:** Anyone can add and assign.',
          { text: '**Tickets:** e.g. Paul (marketing) updates ticket counts; give him promoter-like permission + manager with limited access (no financials).', highlight: 'sidepiece' },
          'If it\'s not in the system: "It wasn\'t added or you\'re not shared on it — so add it and share who needs it."',
        ],
      },
    ],
  },
  {
    id: 'product-ux',
    number: 8,
    icon: LayoutDashboard,
    title: 'Product & UX (dashboards, financials, modeling)',
    color: 'zinc',
    blocks: [
      { type: 'para', text: '**Scenario modeling:** e.g. 10 shows, model "if all sold out" — revenue, expenses, cash flow; only you see until you share. "If you confirm all these shows, here\'s where your money would be." Use this to excite teams.' },
      { type: 'para', text: '**Financials:** Off by default when inviting; turning on = explicit action + warning: "By enabling this you are giving access to financial data."' },
      { type: 'para', text: 'Everyone works in their own dashboard; sharing = link to artist + add team. Draft/unconfirmed: promoter (and artist) need to work on shows in their own space before inviting others.' },
    ],
  },
  {
    id: 'qa-readiness',
    number: 9,
    icon: TestTube,
    title: 'QA & readiness',
    color: 'amber',
    blocks: [
      { type: 'para', text: 'Heavy QA before rollout: test every path a user could take; fill gaps. When promoter (e.g. Thomas Turner) invites many people, flow must be bulletproof.' },
      {
        type: 'list',
        items: [
          { text: 'Test promoter / manager / support flows and their dashboards.', highlight: 'ryan' },
          { text: 'Test the 4 account types that SIDEPIECE would use and how they interact.', highlight: 'sidepiece' },
        ],
      },
    ],
  },
  {
    id: 'white-glove',
    number: 10,
    icon: Heart,
    title: 'White glove & community',
    color: 'rose',
    blocks: [
      { type: 'para', text: '"We can provide the same handholding" — add support to your team with the access you\'re comfortable with; we help update and keep the account in check. Not just email — e.g. WhatsApp: "Text us if you need something."' },
      { type: 'para', text: 'If funded: Amazon-style — win market first; community manager = proactively reach out, "How can we help you use this more?", "Have you tried this feature?" First users get white glove; later same journey via product.' },
    ],
  },
  {
    id: 'order-at-glance',
    number: 11,
    icon: ListOrdered,
    title: 'Order at a glance',
    color: 'sky',
    blocks: [
      {
        type: 'table',
        headers: ['Order', 'Area', 'When'],
        rows: [
          ['1', 'Presentation & marketing (release links, brand, pilot)', 'Ongoing'],
          ['2', 'Onboarding & customer journey (email, follow-up, segments)', 'Define now; automate later'],
          ['3', 'Rollout strategy: promoter-first (one promoter \u2192 managers \u2192 ticket emails)', 'Before scaling'],
          ['4', 'Timeline: ~1 wk SIDEPIECE, 1–2 wk promoter, March 20 go-time', 'Next 2–3 weeks'],
          ['5', 'SIDEPIECE onboarding (accounts, data, calendar, no sheet)', 'Week 1'],
          ['6', 'Who updates what (shows, music, tasks, tickets) + one-sheet', 'With onboarding'],
          ['7', 'Product: financials off by default, dashboards, draft/sharing, scenario modeling', 'Before / during rollout'],
          ['8', 'QA: promoter/manager/support + 4 SIDEPIECE account types', 'Before promoter blast'],
          ['9', 'White glove + WhatsApp + community manager (if funded)', 'From day one for first users'],
        ],
      },
    ],
  },
]

function renderInlineBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
    ) : (
      part
    )
  )
}

function ListItemContent({
  item,
  highlight,
}: {
  item: string | { text: string; highlight?: HighlightType }
  highlight?: HighlightType
}) {
  const text = typeof item === 'string' ? item : item.text
  const h = highlight ?? (typeof item === 'object' ? item.highlight : undefined)
  const isSidepiece = h === 'sidepiece'
  const isRyan = h === 'ryan'
  return (
    <li
      className={`grid grid-cols-[14px_1fr] gap-3 ${
        isSidepiece ? 'rounded-r-lg border-l-4 border-violet-500/80 bg-violet-500/10 pl-2 py-2 -mx-1 pr-1' : ''
      } ${isRyan ? 'rounded-r-lg border-l-4 border-amber-500/80 bg-amber-500/10 pl-2 py-2 -mx-1 pr-1' : ''}`}
    >
      <span className="text-zinc-500 leading-[1.7] pt-[2px]">{'\u2022'}</span>
      <div className="min-w-0">
        {(isSidepiece || isRyan) && (
          <span
            className={`inline-block mb-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${
              isSidepiece ? 'bg-violet-500/25 text-violet-200' : 'bg-amber-500/25 text-amber-200'
            }`}
          >
            {isSidepiece ? 'SIDEPIECE' : 'Ryan — do now'}
          </span>
        )}
        <span className="text-[13.5px] text-zinc-400 leading-[1.7]">{renderInlineBold(text)}</span>
      </div>
    </li>
  )
}

export default function PlanRolloutPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === MASTER_PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-10" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">
                Rollout Plan
              </span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view rollout & customer journey notes.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/40 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/30 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.05),transparent_45%),radial-gradient(circle_at_70%_80%,rgba(255,35,0,0.06),transparent_55%)]" />
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/[0.06]" style={{ backgroundColor: 'rgba(19,18,10,0.82)' }}>
        <div className="max-w-6xl mx-auto px-8 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Rollout Plan
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-[11px] text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-violet-500/80" />
              SIDEPIECE
            </span>
            <span className="flex items-center gap-1.5 text-[11px] text-zinc-500">
              <span className="w-2 h-2 rounded-full bg-amber-500/80" />
              Ryan — do now
            </span>
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-8 sm:px-10 py-16 space-y-16">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-white">Rollout & Customer Journey — Call Notes</h1>
          <p className="text-[13px] text-zinc-500 mt-1 italic">
            Presentation, onboarding, promoter-first rollout, SIDEPIECE setup, who updates what, QA, white glove. <span className="text-violet-400">SIDEPIECE</span> and <span className="text-amber-400">Ryan immediate actions</span> are highlighted.
          </p>
        </div>

        <nav className="flex flex-wrap gap-2.5">
          {sections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3.5 py-2 rounded-full text-[11px] font-medium border transition-all hover:bg-white/[0.03] ${c.border} bg-transparent text-zinc-400`}
              >
                {s.number}. {s.title.replace(/ —.*$/, '').slice(0, 28)}
                {s.title.length > 28 ? '…' : ''}
              </button>
            )
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        <div className="space-y-6">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-2xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-8 py-7 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                    </div>
                    {section.subtitle && (
                      <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-8 pb-10 space-y-7">
                    {section.blocks.map((block, bi) => {
                      if (block.type === 'para') {
                        return (
                          <p key={bi} className="text-[13.5px] text-zinc-400 font-medium leading-relaxed">
                            {renderInlineBold(block.text)}
                          </p>
                        )
                      }
                      if (block.type === 'heading') {
                        const Tag = block.level === 1 ? 'h3' : 'h4'
                        return (
                          <Tag
                            key={bi}
                            className={
                              block.level === 1
                                ? 'text-[16px] font-semibold text-white mt-8 first:mt-0 tracking-tight'
                                : 'text-[14px] font-semibold text-zinc-300 mt-6'
                            }
                          >
                            {block.text}
                          </Tag>
                        )
                      }
                      if (block.type === 'subheading') {
                        return (
                          <p key={bi} className="text-[13px] font-semibold text-zinc-300 mt-6 tracking-tight">
                            {block.text}
                          </p>
                        )
                      }
                      if (block.type === 'list') {
                        return (
                          <ul key={bi} className="space-y-3 list-none">
                            {block.items.map((item, i) => (
                              <ListItemContent key={i} item={item} />
                            ))}
                          </ul>
                        )
                      }
                      if (block.type === 'table') {
                        return (
                          <div key={bi} className="overflow-x-auto rounded-lg border border-white/[0.06]">
                            <table className="w-full text-left text-[12px]">
                              <thead>
                                <tr className="border-b border-white/[0.06]">
                                  {block.headers.map((h, i) => (
                                    <th
                                      key={i}
                                      className="px-4 py-3 text-zinc-500 font-semibold uppercase tracking-wider"
                                    >
                                      {h}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {block.rows.map((row, ri) => (
                                  <tr key={ri} className="border-b border-white/[0.04] last:border-0">
                                    {row.map((cell, ci) => (
                                      <td key={ci} className="px-4 py-3 text-zinc-400">
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                      return null
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <footer className="pt-8 border-t border-white/[0.06] flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700 italic">Source: Rollout & customer journey call notes.</span>
        </footer>
      </main>
    </div>
  )
}
